import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state } from 'app/pwa/app.cerebral';
import dayjs from 'dayjs';

import {
  Wrapper,
  ProfileWrapper,
  Header,
  CurrentPositionWrapper,
  SmallText,
  MainText,
  PositionChangeWrapper,
  PositionChangeLabel,
} from './elements';

import { useText } from 'common/hooks';

type Props = {
  readonly newPosition: number;
  readonly previousPosition: number;
  readonly datetime: number;
};

const deps = {
  isLoadingTeamsData: state.challenge.isLoading.teamsData,
  isLoadingChallengeData: state.challenge.isLoading.challengeData,
  teamName:
    state.challenge.campaign.structure.sections[
      state.challenge.userCampaign.sectionKey
    ].teams[state.challenge.userCampaign.teamKey].name,
  indicator: state.challenge.campaign.indicator,
};

export function PositionChanged({
  isLoadingTeamsData,
  isLoadingChallengeData,
  newPosition,
  previousPosition,
  teamName,
  datetime,
  indicator,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();

  if (isLoadingTeamsData || isLoadingChallengeData) {
    return null;
  }

  const positionDecreased = newPosition > previousPosition;
  const positionIncreased = newPosition < previousPosition;

  function getPositionChangeIcon() {
    if (positionDecreased) {
      return 'icon-arrow_downward';
    }

    if (positionIncreased) {
      return 'icon-arrow_upward';
    }

    return 'icon-arrow_forward';
  }

  function getPositionChangeText() {
    if (positionDecreased) {
      return t(
        'challenge.latest_events.FeC006.position_decreased_from_yesterday',
        {
          smart_count: newPosition - previousPosition,
        }
      );
    }

    if (positionIncreased) {
      return t(
        'challenge.latest_events.FeC006.position_increased_from_yesterday',
        {
          smart_count: previousPosition - newPosition,
        }
      );
    }

    return t('challenge.latest_events.FeC006.position_similar_to_yesterday');
  }

  return (
    <Wrapper>
      <ProfileWrapper>
        <Header
          rightIcon={newPosition === 1 ? 'icon-crown' : 'icon-leaderboard'}
          leftIcon="icon-team"
          title={teamName}
          time={dayjs(datetime).fromNow()}
        />
      </ProfileWrapper>
      <CurrentPositionWrapper positionDecreased={positionDecreased}>
        <SmallText positionDecreased={positionDecreased}>
          {t(
            `challenge.latest_events.FeC006.new_position_on_toplist.${indicator}`
          )}
        </SmallText>
        <MainText positionDecreased={positionDecreased}>
          {t('challenge.latest_events.FeC006.new_team_rank', {
            position: newPosition,
          })}
        </MainText>
      </CurrentPositionWrapper>
      <PositionChangeWrapper>
        <PositionChangeLabel icon={getPositionChangeIcon()}>
          {getPositionChangeText()}
        </PositionChangeLabel>
      </PositionChangeWrapper>
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, PositionChanged);
